import get from 'lodash.get';
import { useHelpStore } from '@/stores/Help.store';

const CDN = 'cdn-public';
const SUPPORT_SERVICE = 'supportapp_service';
// List was obtained from ElasticSearch site:
// https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-query-string-query.html#_reserved_characters
const RESERVED_CHARS = ['\\+', '-', '=', '&', '\\|', '<', '>', '!', '\\(', '\\)', '{', '}', '\\[', '\\]', '\\^',
  '"', '~', '\\*', '\\?', ':', '\\\\', '/', '%'];

export default function helpServiceFactory(services) {

  let thisService = {
    init,
    getTopic,
    getTopicsAsync,
    getTopics,
    getAllTopics,
    getLKCTopics,
    openTopic,
    openTopicById,
    sanitizeSearchText
  };

  let helpStore;
  let helpPromise;
  let appIds;
  return thisService;

  ////////////////////
  function init() {
      helpStore = useHelpStore();
      return services.session.isAuthenticated() ? getTopicsAsync() : Promise.resolve();
  }

  function getTopicsAsync(searchText) {
    if ( services.session.isDevice()) {
      return Promise.resolve(categorizeTopics([]));
    }

    if (helpPromise) {
      return helpPromise;
    }

    if (!searchText && helpStore.categorized ) {
      return Promise.resolve(helpStore.categorized);
    }

    appIds = services.app.getCurrentAppAliases();
    const config = {
      params: {
        active: true,
        locale: services.i18n.getLocale()
      }
    };

    if (searchText) {
      config.params.query = searchText;
    }

    helpPromise = services.http.get(SUPPORT_SERVICE, 'tutorials', config).then(resp => {
      let topics = get(resp, 'data.list', []);
      const categorized = categorizeTopics(topics);
      if (!searchText) {
        // Only cache if everything (not subset)
        helpStore.setTopics(topics);
        helpStore.setCategorized(categorized);
      }

      helpPromise = null;
      return categorized;
    })
      .catch(e => {
        console.warn('Error getting help topics', e);
        helpPromise = null;
        return Promise.reject(e);
      });

    return helpPromise;
  }

  function getLKCTopics(searchText) {
    let lkcApp = services.app.getAppById('lkc');

    if(lkcApp) {
      const config = {
        cache: true,
        params: {
          offset: 0,
          limit: 50
        }
      };

      if (searchText) {
        config.params.query = searchText;
      }

      return services.http.get(SUPPORT_SERVICE, 'lkctutorials', config).then(resp => {
        return get(resp, 'data.items', []);
      });
    } else {
      return Promise.resolve([]);
    }
  }

  function getAllTopics() {
    if (!helpStore.topics) {
      return null;
    }

    return helpStore.topics;
  }

  function getTopics() {
    if (!helpStore.topics) {
      return null;
    }

    return categorizeTopics(helpStore.topics);
  }

  function categorizeTopics(topics) {
    const pageId = services.app.getPageId();
    let currentTopics = {
      page: [],
      app: [],
      more: [],
    };

    topics.forEach(topic => {
      topic.new = isNewFeature(topic);
      if (!topic.contexts && topic.overallSystem) {
        // Anonymous?
        currentTopics.more.push(topic);
      }
      else {
        topic.isApp = validForApp(topic);
        topic.isPage = validForPage(topic, pageId);

        if (topic.isPage) {
          currentTopics.page.push(topic);
          currentTopics.app.push(topic);
          currentTopics.more.push(topic);
        }
        else if (topic.isApp) {
          currentTopics.page.push(topic);
          currentTopics.app.push(topic);
          currentTopics.more.push(topic);
        }
        else if (topic.overallSystem) {
          currentTopics.more.push(topic);
        }
      }
    });

    currentTopics = sortTopicsForPage(currentTopics);
    return currentTopics;
  }

  function validForApp(topic) {
    for (let i = 0; i < topic.contexts.length; i++) {
      if (appIds.includes(topic.contexts[i].app)) {
        return true;
      }
    }

    return false;
  }

  function validForPage(topic, pageId) {
    for (let i = 0; i < topic.contexts.length; i++) {
      if (appIds.includes(topic.contexts[i].app) && topic.contexts[i].page === pageId) {
        return true;
      }
    }

    return false;
  }

  function getTopic(referenceId) {
    if (!helpStore.topics) {
      return null;
    }

    return helpStore.topics.find(topic => {
      return topic.analyticsTitle === referenceId || topic.id === referenceId;
    });
  }

  function openTopicById(id) {
    let myTopic = getTopic(id);
    if (myTopic) {
      openTopic(myTopic);
    }
    else {
      console.log('Topic not found', id);
    }
  }

  function openTopic(topic) {
    switch (topic.type) {
      case 'freshdesk': {
        services.window.open(topic.content, topic.id);
        break;
      }
      case 'golink': {
        services.go.toContextWithLocation(topic.content);
        break;
      }
      case 'url': {
        if (topic.content.includes(CDN)) {
          services.go.toPopupContext('home-help', topic.id);
        } else {
          services.window.open(topic.content, topic.id);
        }
        break;
      }
      case 'pdf': {
        services.go.toPopupContext('home-help', topic.id);
        break;
      }
      case 'video': {
        if (topic.content.includes(CDN)) {
          services.go.toPopupContext('home-help', topic.id);
        } else {
          services.window.open(topic.content, topic.id);
        }
        break;
      }
      default: {
        services.window.open(topic.content, topic.id);
        break;
      }
    }
  }

  function isNewFeature(topic) {
    // Open and close dates required to be considered active
    if (!topic.noveltyOpenDate || !topic.noveltyCloseDate) {
      return false;
    }

    // Some topics have the same open and close date and so are never considered active
    if (topic.noveltyOpenDate === topic.noveltyCloseDate) {
      return false;
    }

    return services.date.isBetween(new Date(), new Date(topic.noveltyOpenDate), new Date(topic.noveltyCloseDate));
  }

  function sortTopicsForPage(topicsForPage) {
    for(let type in topicsForPage) {
      topicsForPage[type] = topicsForPage[type].sort((a,b) => {
        if (a.faq && !b.faq) { return -1; }
        if (!a.faq && b.faq) { return 1; }
        if (a.isPage && !b.isPage) { return -1; }
        if (!a.isPage && b.isPage) { return 1; }
        if (a.score === b.score) {
          return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
        }
        else {
          let s1 = a.score ? a.score : 0;
          let s2 = b.score ? b.score : 0;
          return s2 - s1;
        }
      });
    }
    return topicsForPage;
  }

  function sanitizeSearchText(uncheckedText) {
    const text = (uncheckedText) ? uncheckedText.trim() : '';
    return (text.length === 0) ? text : _removeSpecialChars(text).trim();
  }

  function _removeSpecialChars(text) {
    let sanitizedText = text;
    RESERVED_CHARS.forEach((reservedChar) => {
      sanitizedText = sanitizedText.replace(new RegExp(reservedChar, 'g'), ' ');
    });
    return sanitizedText;
  }
}

